import React, { useEffect, useRef, useState } from 'react';
import { MessageSquare, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const FB_ORIGINS = ['https://www.facebook.com', 'https://web.facebook.com'];

/**
 * Wiederverwendbarer „Mit WhatsApp verbinden"-Button (Meta Embedded Signup).
 * Nutzt die ZENTRALE Plattform-Meta-App. Der Nutzer durchläuft das Meta-Popup
 * und Phone Number ID / WABA / Token werden serverseitig automatisch ermittelt.
 *
 * Props:
 *  - configUrl:   GET-URL, liefert { configured, app_id, config_id, graph_version }
 *  - callbackUrl: POST-URL, erhält { code, waba_id, phone_number_id, business_id }
 *  - headers:     Auth-Header (z. B. { Authorization: 'Bearer ...' })
 *  - onConnected: Callback nach erfolgreicher Verbindung
 *  - testId:      data-testid Prefix
 */
export default function WhatsAppConnectButton({ configUrl, callbackUrl, headers = {}, onConnected, testId = 'wa-connect' }) {
  const [cfg, setCfg] = useState(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const capturedRef = useRef({});

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await fetch(configUrl, { headers });
        if (!r.ok) return;
        const data = await r.json();
        if (!active) return;
        setCfg(data);
        if (data.configured && data.app_id) loadFbSdk(data.app_id, data.graph_version);
      } catch (_) { /* ignore */ }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configUrl]);

  const loadFbSdk = (appId, version) => {
    const v = version || 'v23.0';
    if (window.FB) { setReady(true); return; }
    window.fbAsyncInit = function () {
      window.FB.init({ appId, cookie: true, xfbml: false, version: v });
      setReady(true);
    };
    if (document.getElementById('facebook-jssdk')) { setReady(!!window.FB); return; }
    const js = document.createElement('script');
    js.id = 'facebook-jssdk';
    js.src = 'https://connect.facebook.net/en_US/sdk.js';
    js.async = true; js.defer = true;
    document.body.appendChild(js);
  };

  const onMessage = (event) => {
    if (!FB_ORIGINS.includes(event.origin)) return;
    let d;
    try { d = typeof event.data === 'string' ? JSON.parse(event.data) : event.data; } catch { return; }
    if (!d || d.type !== 'WA_EMBEDDED_SIGNUP') return;
    if (d.data) {
      capturedRef.current = {
        waba_id: d.data.waba_id,
        phone_number_id: d.data.phone_number_id,
        business_id: d.data.business_id,
      };
    }
  };

  const send = async (code) => {
    const info = capturedRef.current || {};
    try {
      const r = await fetch(callbackUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({
          code,
          waba_id: info.waba_id || '',
          phone_number_id: info.phone_number_id || '',
          business_id: info.business_id || '',
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Verbindung fehlgeschlagen');
      toast.success(data.message || 'WhatsApp verbunden!');
      if (onConnected) onConnected(data);
    } catch (e) {
      toast.error(e.message || 'WhatsApp-Verbindung fehlgeschlagen');
    } finally {
      setBusy(false);
      window.removeEventListener('message', onMessage);
    }
  };

  const connect = () => {
    if (!window.FB) { toast.error('Facebook SDK lädt noch. Bitte kurz warten.'); return; }
    setBusy(true);
    capturedRef.current = {};
    window.addEventListener('message', onMessage);
    const opts = { response_type: 'code', override_default_response_type: true };
    if (cfg?.config_id) {
      opts.config_id = cfg.config_id;
    } else {
      opts.scope = 'whatsapp_business_management,whatsapp_business_messaging';
      opts.extras = { feature: 'whatsapp_embedded_signup', version: 2 };
    }
    window.FB.login((response) => {
      if (response && response.authResponse && response.authResponse.code) {
        const code = response.authResponse.code;
        setTimeout(() => send(code), 1200);
      } else {
        setBusy(false);
        window.removeEventListener('message', onMessage);
        toast.error('WhatsApp-Verbindung abgebrochen');
      }
    }, opts);
  };

  const notConfigured = cfg && !cfg.configured;

  return (
    <div>
      <button
        type="button"
        data-testid={`${testId}-btn`}
        onClick={connect}
        disabled={!ready || busy || notConfigured}
        className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquare className="h-4 w-4" />}
        Mit WhatsApp verbinden
      </button>
      {notConfigured && (
        <p data-testid={`${testId}-hint`} className="mt-1.5 text-[11px] text-amber-600">
          Zentrale Meta-App noch nicht hinterlegt. Sobald die Meta-Zugangsdaten gesetzt sind, funktioniert die Ein-Klick-Verbindung.
        </p>
      )}
    </div>
  );
}
